/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USERS_FILTERS_ACTIONS_EXTRACT
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Extracted header action buttons from UsersFilters to keep the parent file
 * under the 200-line component size limit. Uses AdminButton to enforce
 * design-system tokens.
 *
 * File Size Check:
 * - Current: 0
 * - Adding: ~60
 * - Projected: ~60
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-figma-1 / btn-black / btn-danger-figma)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/features/admin/users/UsersFilters.tsx
 */

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTimes, faTrashAlt, faEye } from "@fortawesome/free-solid-svg-icons";
import { Spinner } from "react-bootstrap";
import AdminButton from "../../../components/admin/AdminButton";

interface Props {
  isExporting: boolean;
  previewLoading: boolean;
  cleanupLoading: boolean;
  handleExport: () => void;
  handlePreviewOrphanedData: () => void;
  handleCleanOrphanedData: () => void;
  handleClearFilters: () => void;
  hasActiveFilters: boolean;
}

export default function UsersFiltersActions({
  isExporting,
  previewLoading,
  cleanupLoading,
  handleExport,
  handlePreviewOrphanedData,
  handleCleanOrphanedData,
  handleClearFilters,
  hasActiveFilters,
}: Props) {
  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex gap-2 w-100">
        <AdminButton
          intent="secondary"
          size="sm"
          onClick={handleExport}
          disabled={isExporting}
          className="flex-fill"
          title="Export users to CSV"
        >
          <FontAwesomeIcon icon={faDownload} />{" "}
          <span className="ms-1 d-none d-lg-inline">{isExporting ? "..." : "Export"}</span>
        </AdminButton>

        <AdminButton
          intent="secondary"
          size="sm"
          onClick={handlePreviewOrphanedData}
          disabled={previewLoading || cleanupLoading}
          className="flex-fill"
          title="Preview orphaned data"
        >
          {previewLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              <span className="d-none d-lg-inline">Loading...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faEye} />{" "}
              <span className="ms-1 d-none d-lg-inline">Preview</span>
            </>
          )}
        </AdminButton>

        <AdminButton
          intent="danger"
          size="sm"
          onClick={handleCleanOrphanedData}
          disabled={cleanupLoading}
          className="flex-fill"
          title="Clean orphaned data"
        >
          {cleanupLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              <span className="d-none d-lg-inline">Cleaning...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faTrashAlt} />{" "}
              <span className="ms-1 d-none d-lg-inline">Clean</span>
            </>
          )}
        </AdminButton>
      </div>
      {hasActiveFilters && (
        <AdminButton intent="secondary" size="sm" onClick={handleClearFilters} className="w-100">
          <FontAwesomeIcon icon={faTimes} className="me-1" /> Clear Filters
        </AdminButton>
      )}
    </div>
  );
}

